import { Constants } from '@/constants/announcements';
import {
  IconArrowBackMS,
  IconArrowForwardIosMS,
  IconCloseMS,
  IconPlayCircleMS,
  IconScheduleMS,
} from '@/constants/icons';
import { IconButton } from '@mui/material';
import { useEffect, useState } from 'react';

import { RichTextViewer } from '../RichTextViewer';
import {
  Badge,
  CardDescription,
  CardTitle,
  CloseButton,
  ConfirmButton,
  FooterContainer,
  MediaBox,
  NavigationContainer,
  ProgressContainer,
  ProgressDash,
  StyledDialog,
  ViewLaterButton,
} from './styles';

export interface PreviewCard {
  title: string;
  description: string;
  videoUrl: string | null;
  imageUrl: string | null;
}

interface PreviewModalProps {
  open: boolean;
  cards: PreviewCard[];
  onClose: () => void;
}

export function PreviewModal({
  open,
  cards,
  onClose,
}: PreviewModalProps): JSX.Element {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (open) setCurrent(0);
  }, [open]);

  const card = cards[current];

  const handleOpenVideo = (url: string): void => {
    window.open(url, '_blank', 'noopener');
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <CloseButton onClick={onClose}>{IconCloseMS}</CloseButton>

      <ProgressContainer>
        {cards.map((item, index) => (
          <ProgressDash
            key={`${item.title}-${index + 1}`}
            isActive={index === current}
          />
        ))}
      </ProgressContainer>

      {card && (
        <>
          <Badge>{Constants.previewBadge}</Badge>
          <CardTitle>{card.title}</CardTitle>
          <CardDescription>
            <RichTextViewer content={card.description} />
          </CardDescription>

          {card.imageUrl && (
            <MediaBox>
              <img src={card.imageUrl} alt={card.title} />
            </MediaBox>
          )}

          {card.videoUrl && !card.imageUrl && (
            <MediaBox onClick={() => handleOpenVideo(card.videoUrl ?? '')}>
              <span className="play-icon">{IconPlayCircleMS}</span>
            </MediaBox>
          )}
        </>
      )}

      <NavigationContainer>
        <IconButton
          onClick={() => setCurrent((prev) => prev - 1)}
          disabled={current === 0}
        >
          {IconArrowBackMS}
        </IconButton>

        {Constants.previewCounter(current + 1, cards.length)}

        <IconButton
          onClick={() => setCurrent((prev) => prev + 1)}
          disabled={current >= cards.length - 1}
        >
          {IconArrowForwardIosMS}
        </IconButton>
      </NavigationContainer>

      <FooterContainer>
        <ViewLaterButton onClick={onClose}>
          {IconScheduleMS}
          {Constants.previewViewLater}
        </ViewLaterButton>
        <ConfirmButton onClick={onClose}>
          {Constants.previewConfirm}
        </ConfirmButton>
      </FooterContainer>
    </StyledDialog>
  );
}
